import type { ContentSummary } from '@augment-toolkit/core';
import { Box, Collapse, Typography, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { SDLC_PATH } from '../../config';
import { countLabel } from '../../content';
import { DISCLOSURE_MS, REDUCED_MOTION_QUERY, cardSurfaceSx } from '../../theme';
import type { ProcessCardProps } from './types';

/**
 * A process on the phase page: its number, its method count, its name, and the
 * methods under it.
 *
 * **Every blue thing on this card is a real link, and the card itself is not.** A
 * link inside a clickable card is either an invalid nested anchor or a target
 * that swallows the one inside it, so the card stopped being a link rather than
 * the method names stopping being blue — and it does not lift on hover, because a
 * surface that moves without being clickable is a promise it does not keep.
 *
 * **"and N more" expands the list rather than navigating.** As a link it would be
 * the one control naming the hidden methods that would not show them; expanded in
 * place, the reader stays on the page where twelve processes compare at once.
 *
 * **The count comes from `methodCount`, not from the list**, which is five names
 * of what may be twelve — captioning the card with its own truncation.
 */

/** How many method names a collapsed card shows. */
const VISIBLE_METHODS = 5;

/**
 * Colour comes from the `Typography` around each link, so one style serves all.
 * Underlined on hover rather than always — six underlined lines read as a
 * paragraph of links.
 */
const linkStyle = { color: 'inherit', textDecoration: 'none' } as const;

/**
 * A link that is not a navigation. Styled to match the card's other blue text,
 * because the browser's own button chrome would put a grey box on the card.
 */
const disclosureSx = {
  fontWeight: 500,
  color: 'text.link',
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'left',
  cursor: 'pointer',
  alignSelf: 'flex-start',
  '&:hover': { textDecoration: 'underline' },
} as const;

/** One method in the card's list. The bullet is the component's; the API returns clean names. */
const MethodLine = ({ method }: { method: ContentSummary }) => (
  <Box component="li" sx={{ display: 'flex', gap: 2 }}>
    <Typography
      variant="body2"
      component="span"
      aria-hidden
      sx={{ flexShrink: 0, color: 'text.secondary' }}
    >
      &bull;
    </Typography>
    <Typography
      variant="body2"
      component="span"
      sx={{ color: 'text.link', '&:hover': { textDecoration: 'underline' } }}
    >
      <RouterLink to={`${SDLC_PATH}/${method.path}`} style={linkStyle}>
        {method.name}
      </RouterLink>
    </Typography>
  </Box>
);

export const ProcessCard = ({ process, reference, methods = [] }: ProcessCardProps) => {
  // **`Collapse` animates a height in JavaScript**, so unlike the app's CSS it
  // cannot opt itself out of `prefers-reduced-motion` — the timeout is zeroed
  // from here. The filter sidebar has done this since it was built; this card
  // was the one disclosure that had not.
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const [expanded, setExpanded] = useState(false);

  const processPath = `${SDLC_PATH}/${process.path}`;
  const alwaysVisible = methods.slice(0, VISIBLE_METHODS);
  const collapsed = methods.slice(VISIBLE_METHODS);
  const hidden = collapsed.length;

  return (
    <Box
      sx={{
        ...cardSurfaceSx,
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        // Fills its grid cell. Without it the card hugs its content inside the
        // flex list item and a row comes out at three different widths.
        width: '100%',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
        <Typography
          variant="caption"
          component="span"
          sx={{ fontWeight: 500, letterSpacing: '1px', color: 'text.secondary' }}
        >
          {reference}
        </Typography>

        {process.methodCount !== undefined && (
          <Typography
            variant="caption"
            component="span"
            sx={{
              fontWeight: 500,
              letterSpacing: '1px',
              color: 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            {countLabel(process.methodCount, 'method')}
          </Typography>
        )}
      </Box>

      <Typography variant="h3" component="h3" sx={{ '&:hover': { textDecoration: 'underline' } }}>
        <RouterLink to={processPath} style={linkStyle}>
          {process.name}
        </RouterLink>
      </Typography>

      {alwaysVisible.length > 0 && (
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {alwaysVisible.map((method) => (
            <MethodLine key={method.id} method={method} />
          ))}
        </Box>
      )}

      {hidden > 0 && (
        // **`Collapse` rather than a conditional render**, so the card grows
        // into its height instead of jumping. The row reflows either way; the
        // animation is what makes that read as an expansion, not a glitch.
        <Collapse in={expanded} timeout={prefersReducedMotion ? 0 : DISCLOSURE_MS} unmountOnExit>
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {collapsed.map((method) => (
              <MethodLine key={method.id} method={method} />
            ))}
          </Box>
        </Collapse>
      )}

      {/* Takes the slack so the footer sits on the card's bottom edge. */}
      <Box sx={{ flex: 1 }} />

      {hidden > 0 && (
        <Typography
          component="button"
          type="button"
          variant="caption"
          aria-expanded={expanded}
          onClick={() => {
            setExpanded((open) => !open);
          }}
          sx={disclosureSx}
        >
          {expanded ? (
            <>
              Show fewer <span aria-hidden>&uarr;</span>
            </>
          ) : (
            <>
              and {String(hidden)} more <span aria-hidden>&darr;</span>
            </>
          )}
        </Typography>
      )}
    </Box>
  );
};
